/**
 * Pure rules for the Expense Inventory Sampling table, kept apart from the components so each
 * one is unit-tested.
 * @module src/components/Inventory/expense/detail/ExpenseInventorySampling/samplingLineRules
 */
import { isBefore, isValid, startOfDay } from 'date-fns';
import { EXPENSE_INVENTORY_CASE_STATUS, MAX_QUANTITY } from '../../../../../util/constants';
import { INVENTORY_CHANNELS } from '../../../../Usage/UsageDetails/UsageTabs/Parts/AddParts/useParts';

interface DetailLineQuantity {
  status?: string;
  sourceLocation?: string;
  sfid?: string;
  externalId?: string;
  lotSerial?: string;
  subInventoryCode?: string;
  productId?: string;
  availableQuantity?: string | number | null;
  quantity?: string | number;
}

interface AddPartsLineItem extends DetailLineQuantity {
  lotNumber?: string;
  caseUsageSubInventoryCode?: string;
}

/**
 * The most a line can hold: what is still available plus what the line already holds, as Bill
 * Only caps a lot line. A catalog line, or a line with no inventory row, is not capped, which
 * matches what the save checks.
 */
export const getSamplingMaxQuantity = (line: DetailLineQuantity): number => {
  const hasAvailableQuantity =
    line.availableQuantity !== null &&
    line.availableQuantity !== undefined &&
    line.availableQuantity !== '';
  if (
    line.sourceLocation === INVENTORY_CHANNELS.CATALOG ||
    !line.lotSerial ||
    !hasAvailableQuantity
  ) {
    return MAX_QUANTITY;
  }
  return Math.max(Number(line.availableQuantity) || 0, 0) + (Number(line.quantity) || 0);
};

/** A catalog line is red while its lot is not in inventory (Bill Only's rule). */
export const isSamplingLineRed = (item: {
  iscatalog?: boolean;
  isValidLotSerialCombination?: boolean;
}): boolean => !!item.iscatalog && !item.isValidLotSerialCombination;

/** The catalog icon follows where the part was first added from, so it survives conversion to Repstock. */
export const isAddedFromCatalog = (line: { partAddedFrom?: string | null }): boolean =>
  line.partAddedFrom === INVENTORY_CHANNELS.CATALOG;

/**
 * A saved line can prefill the Add Parts counter only while it is still New (not yet Requested,
 * Submitted or Cancelled, matching B4's New-only fold rule), came from Repstock rather than the
 * Catalog, has synced far enough to carry an id (sfid, or externalId before the first sync), and
 * has both a lot and a sub-inventory to match on.
 */
const isPrefillableSavedLine = (line: DetailLineQuantity): boolean =>
  line.status === EXPENSE_INVENTORY_CASE_STATUS.NEW &&
  line.sourceLocation !== INVENTORY_CHANNELS.CATALOG &&
  !!(line.sfid || line.externalId) &&
  !!line.lotSerial &&
  !!line.subInventoryCode;

/**
 * Maps saved lines onto the shared Add Parts matcher's field names (`lotNumber`/
 * `caseUsageSubInventoryCode`) so a prefillable line's counter starts at its saved quantity, as
 * Bill Only's lines already do. Every other line is returned unchanged, so the catalog matcher,
 * which runs against this same list, sees the same input as today.
 */
export const toAddPartsLineItems = (lineItems?: DetailLineQuantity[] | null): AddPartsLineItem[] =>
  (lineItems ?? []).map(line =>
    isPrefillableSavedLine(line)
      ? { ...line, lotNumber: line.lotSerial, caseUsageSubInventoryCode: line.subInventoryCode }
      : line
  );

/**
 * Finds the saved line, if any, that a counter change in the Add Parts modal would fold into,
 * using the shared matcher's keys: productId, lotNumber, and sub-inventory.
 */
export const findPrefilledSavedLine = (
  part: { productId?: string; lotNumber?: string; subInventoryCode?: string },
  addPartsLineItems: AddPartsLineItem[]
): AddPartsLineItem | undefined =>
  addPartsLineItems.find(
    line =>
      line.productId === part.productId &&
      line.lotNumber === part.lotNumber &&
      line.caseUsageSubInventoryCode === part.subInventoryCode
  );

/** An inventory row of the lots query (getSubInventoryWithLotDetails), as far as the lot options read it. */
export interface CatalogLotInventoryRow {
  lotNumber?: string | null;
  subInventoryCode?: string | null;
  availableQuantity?: string | number | null;
  expirationDate?: string | null;
}

/** A lot choice in the shared InputWithAutoSuggestion, which reads `label` and `availableqty`. */
export interface CatalogLotOption {
  id: string;
  label: string;
  value: string;
  lotNumber: string;
  subInventoryCode: string;
  availableqty: number;
}

// Read in local time, the way the table shows the expiration date and Bill Only reads both dates.
const toCalendarDate = (date?: string | Date | null): Date | null => {
  if (!date) {
    return null;
  }
  const parsedDate = new Date(date);
  if (!isValid(parsedDate)) {
    return null;
  }
  return startOfDay(parsedDate);
};

/**
 * Bill Only's expiration rule with the usage date in the surgery date's place: a lot is expired
 * when it expired before today and before the usage date, or before today when there is none.
 */
export const isLotExpiredForUsageDate = (
  expirationDate?: string | Date | null,
  usageDate?: string | Date | null,
  today: Date = new Date()
): boolean => {
  const expiration = toCalendarDate(expirationDate);
  if (!expiration || !isBefore(expiration, startOfDay(today))) {
    return false;
  }
  const usage = toCalendarDate(usageDate);
  if (!usage) {
    return true;
  }
  return isBefore(expiration, usage);
};

/**
 * The lots a catalog line can pick from, as Bill Only offers them: in stock, not expired against
 * the usage date, one choice per lot or serial even when it sits in several accounts. The account
 * is not chosen here: the line save matches the lot and picks it.
 */
export const buildCatalogLotOptions = (
  rows: CatalogLotInventoryRow[] = [],
  usageDate?: string | null,
  today: Date = new Date()
): CatalogLotOption[] => {
  const options = rows
    .filter(row => Number(row?.availableQuantity) > 0)
    .filter(row => !isLotExpiredForUsageDate(row?.expirationDate, usageDate, today))
    .filter(row => !!row?.lotNumber)
    .map(row => {
      const lotNumber = row.lotNumber as string;
      return {
        id: lotNumber,
        label: lotNumber,
        value: lotNumber,
        lotNumber,
        subInventoryCode: row.subInventoryCode || '',
        availableqty: Number(row.availableQuantity) || 0,
      };
    });
  return options.filter(
    (option, index) => options.findIndex(other => other.id === option.id) === index
  );
};

/**
 * A typed catalog lot is upper-cased, as Bill Only's catalog lot field does (forceUppercase), so
 * the row's lot always equals what the field shows.
 */
export const toCatalogLotInput = (value: string): string => value.toUpperCase();
